// Performance optimization utilities for better SEO rankings

export class PerformanceOptimizer {
  private static instance: PerformanceOptimizer;
  
  static getInstance(): PerformanceOptimizer {
    if (!PerformanceOptimizer.instance) {
      PerformanceOptimizer.instance = new PerformanceOptimizer();
    }
    return PerformanceOptimizer.instance;
  }

  // Preload critical resources
  preloadCriticalResources(): void {
    // Disable resource preloading to avoid "unused preload" warnings
    // This can be re-enabled later with proper resource detection
    return;
  }

  private preloadResource(href: string, as: string, type?: string): void {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (type) link.type = type;
    if (as === 'font') link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  // Implement lazy loading for images
  setupIntersectionObserver(): IntersectionObserver {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.dataset.src;
          if (src) {
            img.src = src;
            img.classList.remove('lazy');
            observer.unobserve(img);
          }
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01
    });

    return imageObserver;
  }

  // Optimize images with WebP format
  getOptimizedImageUrl(originalUrl: string, width?: number, quality = 80): string {
    // If using a CDN like Cloudinary or ImageKit, add transformation parameters
    const url = new URL(originalUrl);
    
    // Add width parameter if specified
    if (width) {
      url.searchParams.set('w', width.toString());
    }
    
    // Add quality parameter
    url.searchParams.set('q', quality.toString());
    
    // Request WebP format with fallback
    url.searchParams.set('f', 'webp');
    
    return url.toString();
  }

  // Implement resource hints
  addResourceHints(): void {
    // DNS prefetch for external domains
    this.addDNSPrefetch('https://fonts.googleapis.com');
    this.addDNSPrefetch('https://www.google-analytics.com');
    this.addDNSPrefetch('https://pagead2.googlesyndication.com');
    
    // Preconnect to critical origins
    this.addPreconnect('https://fonts.gstatic.com');
    this.addPreconnect('https://firebasestorage.googleapis.com');
  }

  private addDNSPrefetch(href: string): void {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = href;
    document.head.appendChild(link);
  }

  private addPreconnect(href: string): void {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  // Measure and report Core Web Vitals
  measureCoreWebVitals(): void {
    // Largest Contentful Paint (LCP)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      console.log('LCP:', lastEntry.startTime);
      
      // Report to analytics
      this.reportMetric('LCP', lastEntry.startTime);
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay (FID)
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        const fidEntry = entry as any;
        console.log('FID:', fidEntry.processingStart - fidEntry.startTime);
        
        // Report to analytics
        this.reportMetric('FID', fidEntry.processingStart - fidEntry.startTime);
      });
    }).observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        const clsEntry = entry as any;
        if (!clsEntry.hadRecentInput) {
          clsValue += clsEntry.value;
        }
      });
      
      console.log('CLS:', clsValue);
      
      // Report to analytics
      this.reportMetric('CLS', clsValue);
    }).observe({ entryTypes: ['layout-shift'] });
  }

  private reportMetric(name: string, value: number): void {
    // Report to Google Analytics 4
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', name, {
        event_category: 'Web Vitals',
        value: Math.round(value),
        non_interaction: true
      });
    }
  }

  // Implement service worker for caching
  registerServiceWorker(): void {
  // Only register in production where sw.js is available
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then(registration => {
            console.log('SW registered: ', registration);
          })
          .catch(registrationError => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }
  }

  // Optimize bundle loading
  setupModulePreloading(): void {
    // Disable module preloading to avoid MIME type errors
    // This feature can be re-enabled later with proper route detection
    return;
  }

  // Implement critical CSS inlining
  inlineCriticalCSS(): void {
    const criticalCSS = `
      /* Critical above-the-fold styles */
      .header { /* header styles */ }
      .hero { /* hero section styles */ }
      .wallpaper-grid { /* grid layout styles */ }
    `;

    const style = document.createElement('style');
    style.textContent = criticalCSS;
    document.head.appendChild(style);
  }
}

// Export singleton instance
export const performanceOptimizer = PerformanceOptimizer.getInstance();